import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMedico } from "@/hooks/useIsMedico";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronDown, Copy, Link, Trash2, User, Video, X } from "lucide-react";
import { useState } from "react";
import { toast as sonnerToast } from "sonner";

interface MockAppointment {
  id: number;
  id_paciente: number;
  paciente_nome?: string;
  id_tipo_agenda: number;
  tipo_agenda_descricao?: string;
  id_medico: number;
  medico_nome?: string;
  data_agendamento: string;
  hora_inicio: string;
  hora_fim: string;
  sala_consultorio?: string;
  motivo?: string;
  observacoes?: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  status_agendamento: 'AGENDADO' | 'CONFIRMADO' | 'CANCELADO' | 'AGUARDANDO_CONFIRMACAO' | 'CONCLUIDO';
  duracao_minutos: number;
  meet_link?: string;
  notificacao_sms: boolean;
  notificacao_whatsapp: boolean;
  notificacao_email: boolean;
}

interface TipoAtendimentoAgenda {
  id: number;
  nome: string;
  cor?: string;
  habilitado_meet?: boolean;
}

interface ListaAgendaProps {
  appointments: MockAppointment[];
  doctors: string[];
  filterDoctor: string[];
  filterStatus: string;
  filterType: string;
  tiposAtendimento?: TipoAtendimentoAgenda[];
  isGoogleMeetAutoEnabled: boolean;
  onFilterDoctorChange: (value: string[]) => void;
  onFilterStatusChange: (value: string) => void;
  onFilterTypeChange: (value: string) => void;
  onUpdateAppointment: (id: number, updates: Partial<MockAppointment>) => void;
  onCreateMeetLink: (id: number) => Promise<void>;
  onDeleteAppointment?: (id: number) => void;
}

const getStatusBadge = (status: string) => {
  const statusMap: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    AGENDADO: { label: "Agendado", variant: "default" },
    CONFIRMADO: { label: "Confirmado", variant: "secondary" },
    CANCELADO: { label: "Cancelado", variant: "destructive" },
    AGUARDANDO_CONFIRMACAO: { label: "Aguardando", variant: "outline" },
    CONCLUIDO: { label: "Concluído", variant: "secondary" },
  };
  const config = statusMap[status] || statusMap.AGENDADO;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getPriorityBadge = (priority: string) => {
  const priorityMap: Record<string, { label: string; className: string }> = {
    BAIXA: { label: "Baixa", className: "bg-green-500/10 text-green-700 dark:text-green-400" },
    MEDIA: { label: "Média", className: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400" },
    ALTA: { label: "Alta", className: "bg-red-500/10 text-red-700 dark:text-red-400" },
  };
  const config = priorityMap[priority] || priorityMap.MEDIA;
  return <Badge className={config.className}>{config.label}</Badge>;
};

export const ListaAgenda = ({
  appointments,
  doctors,
  filterDoctor,
  filterStatus,
  filterType,
  tiposAtendimento = [],
  isGoogleMeetAutoEnabled,
  onFilterDoctorChange,
  onFilterStatusChange,
  onFilterTypeChange,
  onUpdateAppointment,
  onCreateMeetLink,
  onDeleteAppointment,
}: ListaAgendaProps) => {
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  // Verificação de permissões e cargo
  const { isAdmin } = usePermissions();
  const { isMedico } = useIsMedico();

  const handleStartAppointment = (meetLink?: string) => {
    if (!meetLink) return;
    window.open(meetLink, "_blank", "noopener,noreferrer");
  };

  const copyMeetLink = async (meetLink?: string) => {
    if (!meetLink) return;
    try {
      await navigator.clipboard.writeText(meetLink);
      sonnerToast.success("Link copiado!", { description: "Link da video conferência copiado para a área de transferência." });
    } catch {
      sonnerToast.error("Erro ao copiar", { description: "Não foi possível copiar o link." });
    }
  };

  const isMeetEnabledForType = (idTipoAtendimento: number): boolean => {
    const tipoAtendimento = tiposAtendimento.find(tipo => tipo.id === idTipoAtendimento);
    return Boolean(tipoAtendimento?.habilitado_meet);
  };

  const handleDoctorToggle = (doctor: string) => {
    if (filterDoctor.includes(doctor)) {
      onFilterDoctorChange(filterDoctor.filter((d) => d !== doctor));
    } else {
      onFilterDoctorChange([...filterDoctor, doctor]);
    }
  };

  const handleSelectAll = () => {
    if (filterDoctor.length === doctors.length) {
      onFilterDoctorChange([]);
    } else {
      onFilterDoctorChange([...doctors]);
    }
  };

  const handleClearPeriod = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const filteredAppointments = appointments
    .filter((apt) => {
      const matchesDoctor = filterDoctor.length === 0 || filterDoctor.includes(apt.medico_nome || "");
      const matchesStatus = filterStatus === "all" || apt.status_agendamento === filterStatus;
      const matchesType = filterType === "all" || apt.id_tipo_agenda.toString() === filterType || apt.tipo_agenda_descricao?.toLowerCase().includes(filterType.toLowerCase());

      // Filtro por período
      let matchesPeriod = true;
      if (dateFrom || dateTo) {
        const aptDate = new Date(apt.data_agendamento);
        aptDate.setHours(0, 0, 0, 0);

        if (dateFrom) {
          const fromDate = new Date(dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (aptDate < fromDate) {
            matchesPeriod = false;
          }
        }

        if (dateTo) {
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (aptDate > toDate) {
            matchesPeriod = false;
          }
        }
      }

      return matchesDoctor && matchesStatus && matchesType && matchesPeriod;
    })
    .sort((a, b) => {
      const dateCompare = a.data_agendamento.localeCompare(b.data_agendamento);
      return dateCompare !== 0 ? dateCompare : a.hora_inicio.localeCompare(b.hora_inicio);
    });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Todos os Agendamentos</CardTitle>
        <div className="flex gap-2 mt-4 flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[250px] justify-between">
                <span>
                  {filterDoctor.length === 0
                    ? "Todos os médicos"
                    : filterDoctor.length === 1
                      ? filterDoctor[0]
                      : `${filterDoctor.length} médico(s) selecionado(s)`}
                </span>
                <ChevronDown className="h-4 w-4 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[250px] p-0" align="start">
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-semibold">Selecionar Médicos</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAll}
                    className="h-8 text-xs"
                  >
                    {filterDoctor.length === doctors.length ? "Desmarcar todos" : "Selecionar todos"}
                  </Button>
                </div>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {doctors.map((doctor) => (
                    <div key={doctor} className="flex items-center space-x-2">
                      <Checkbox
                        id={`doctor-${doctor}`}
                        checked={filterDoctor.includes(doctor)}
                        onCheckedChange={() => handleDoctorToggle(doctor)}
                      />
                      <Label
                        htmlFor={`doctor-${doctor}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        {doctor}
                      </Label>
                    </div>
                  ))}
                </div>
                {filterDoctor.length > 0 && (
                  <div className="pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onFilterDoctorChange([])}
                      className="w-full text-xs"
                    >
                      Limpar seleção
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {filterDoctor.length > 0 && (
            <div className="flex gap-1 flex-wrap">
              {filterDoctor.map((doctor) => (
                <Badge
                  key={doctor}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {doctor}
                  <button
                    onClick={() => handleDoctorToggle(doctor)}
                    className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <Select value={filterType} onValueChange={onFilterTypeChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Todos os tipos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os tipos</SelectItem>
              {tiposAtendimento.map((tipo) => (
                <SelectItem key={tipo.id} value={tipo.id.toString()}>
                  {tipo.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={onFilterStatusChange}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Todos os status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="AGENDADO">Agendado</SelectItem>
              <SelectItem value="CONFIRMADO">Confirmado</SelectItem>
              <SelectItem value="CANCELADO">Cancelado</SelectItem>
              <SelectItem value="AGUARDANDO_CONFIRMACAO">Aguardando Confirmação</SelectItem>
              <SelectItem value="CONCLUIDO">Concluído</SelectItem>
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-[280px] justify-start text-left font-normal",
                  !dateFrom && !dateTo && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateFrom && dateTo ? (
                  <>
                    {format(dateFrom, "dd/MM/yyyy", { locale: ptBR })} - {format(dateTo, "dd/MM/yyyy", { locale: ptBR })}
                  </>
                ) : dateFrom ? (
                  format(dateFrom, "dd/MM/yyyy", { locale: ptBR })
                ) : (
                  "Filtrar por período"
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-4 space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Data Inicial</Label>
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={setDateFrom}
                    locale={ptBR}
                    initialFocus
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Data Final</Label>
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={setDateTo}
                    locale={ptBR}
                    disabled={(date) => dateFrom ? date < dateFrom : false}
                  />
                </div>
                {(dateFrom || dateTo) && (
                  <div className="pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearPeriod}
                      className="w-full text-xs"
                    >
                      Limpar período
                    </Button>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {(dateFrom || dateTo) && (
            <Badge
              variant="secondary"
              className="flex items-center gap-1"
            >
              {dateFrom && dateTo
                ? `${format(dateFrom, "dd/MM/yyyy", { locale: ptBR })} - ${format(dateTo, "dd/MM/yyyy", { locale: ptBR })}`
                : dateFrom
                  ? `A partir de ${format(dateFrom, "dd/MM/yyyy", { locale: ptBR })}`
                  : `Até ${format(dateTo!, "dd/MM/yyyy", { locale: ptBR })}`}
              <button
                onClick={handleClearPeriod}
                className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {filteredAppointments.map((apt) => (
            <Card key={apt.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-2 flex-1">
                  <div className="flex items-center gap-3">
                    <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-semibold">
                      {format(new Date(apt.data_agendamento), "dd/MM/yyyy", { locale: ptBR })} às {apt.hora_inicio}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{apt.paciente_nome}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <p>Médico: {apt.medico_nome}</p>
                    <p>Tipo: {apt.tipo_agenda_descricao || `Tipo ${apt.id_tipo_agenda}`}</p>
                  </div>
                  <div className="flex gap-2">
                    {getStatusBadge(apt.status_agendamento)}
                  </div>
                </div>
                <div className="flex gap-2">
                  {!apt.meet_link &&
                    !isGoogleMeetAutoEnabled &&
                    apt.status_agendamento !== "CANCELADO" &&
                    isMeetEnabledForType(apt.id_tipo_agenda) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onCreateMeetLink(apt.id)}
                        className="gap-2"
                      >
                        <Link className="h-4 w-4" />
                        Criar link Meet
                      </Button>
                    )}
                  {apt.meet_link && apt.status_agendamento !== "CANCELADO" && (isAdmin || isMedico) && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => handleStartAppointment(apt.meet_link)}
                        className="gap-2"
                      >
                        <Video className="h-4 w-4" />
                        Iniciar atendimento
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyMeetLink(apt.meet_link)}
                        className="gap-2"
                        title="Copiar link da video conferência"
                      >
                        <Copy className="h-4 w-4" />
                        Copiar link
                      </Button>
                    </>
                  )}
                  {apt.status_agendamento === "AGENDADO" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onUpdateAppointment(apt.id, { status_agendamento: "CONFIRMADO" })}
                    >
                      Confirmar
                    </Button>
                  )}
                  {apt.status_agendamento !== "CANCELADO" && apt.status_agendamento !== "CONCLUIDO" && (
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => onUpdateAppointment(apt.id, { status_agendamento: "CANCELADO" })}
                    >
                      Cancelar
                    </Button>
                  )}
                  {onDeleteAppointment && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="destructive" className="px-2" title="Excluir Agendamento">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Esta ação não pode ser desfeita. O agendamento {apt.paciente_nome ? `de ${apt.paciente_nome}` : ''} será apagado permanentemente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onDeleteAppointment(apt.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Excluir
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

